import React, { useState, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';

// Components 

import Box from '@mui/material/Box';
import InfoBox from './InfoBox';
import AddIcon from '@mui/icons-material/Add';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import { createConsultation } from '../../../../../../routes/doctor/consultation';
import { setConsultationId } from '../../../../../../redux/features/doctor/Dashboard/consultation/consultation';
import { setIsSuspendedGroupPopupOpened } from '../../../../../../redux/features/general/navigation';

const Info: React.FC = () => {

    const dispatch = useAppDispatch(); 
    const { t } = useTranslation(); 
    const navigate = useNavigate(); 
    const isMobile = useMediaQuery('(max-width: 768px)'); // Breakpoint for stacking InfoBoxes

    const colors = useAppSelector(state => state.theme.colors); 

    // --

    const handleConsultationCreation = async () => { 

        const res = await createConsultation() as any; 

        if (res?.data.status === 200) { 

            dispatch(setConsultationId(res.data.consultation._id)); 

            // Navigate to the actual consultation page - adjust the route as necessary
            // Assuming the route includes consultation ID and potentially a note ID
            navigate(`/consultation/${res.data.consultation._id}/note/${res.data.consultationNote._id}`);

        } else if (res.data.status === 401) { 

            if (res.data.error === 'suspendedGroup') { 

                dispatch(setIsSuspendedGroupPopupOpened(true)); 

            }; 

        }; 

    }; 

    // --

    return (

      <Box sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center', 
          width: '100%', // Use full width of parent
          maxWidth: '900px', // Max width for the info section
          height: 'auto', 
          border: '1px solid rgba(184, 184, 184, 0.3)', // Thinner border
          color: colors?.text,
          borderRadius: 2, 
          padding: isMobile ? 1.5 : 6, // Adjust padding for mobile
          fontSize: 14, 
          textAlign: 'center', // Center align text content
      }}>

        <Box
            sx={{ 
                fontWeight: 'bold',
                fontSize: isMobile ? 16 : 18, // Adjust font size
            }}
        >{t('startConsultation')}
        </Box>

        <Box
            sx={{ 
                marginTop: 1, // Reduce margin
                marginBottom: isMobile ? 1 : 3, 
                maxWidth: '600px', // Limit width of description text
                fontSize: isMobile ? 13 : 14, // Adjust font size
            }}
        >{t('startConsultationInfo')}</Box>

        {/* Container for InfoBox components */}
        <Box
            sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row', // Stack vertically on mobile
                justifyContent: 'space-around', // Space out horizontally
                alignItems: isMobile ? 'center' : 'flex-start', // Align center on mobile, start on desktop
                marginTop: isMobile ? 1 : 3,
                marginBottom: isMobile ? 1 : 3,  
                width: '100%', 
                gap: isMobile ? 1.5 : 2, // Add gap between items, larger when stacked
            }}
        >

            <InfoBox 
                icon={<NoteAddOutlinedIcon sx={{ color: colors?.main }} />}
                title={'create'}
                description={'createConsultationInfo'}
            />

            <InfoBox 
                icon={<GraphicEqIcon sx={{ color: colors?.main }} />}
                title={'discuss'}
                description={'discussConsultationInfo'}
            />

            <InfoBox 
                icon={<AutoAwesomeIcon sx={{ color: colors?.main }} />}
                title={'complete'}
                description={'completeConsultationInfo'}
            />

        </Box>

        {/* Create Button */}
        <Box
            onClick={handleConsultationCreation}
            sx={{ 
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center', 
                justifyContent: 'center', // Center button content
                padding: 0.5, 
                fontSize: isMobile ? 14 : 16, // Adjust font size
                borderRadius: 1, 
                marginTop: isMobile ? 1 : 3, 
                paddingLeft: 2,
                paddingRight: 2, 
                minWidth: isMobile ? '180px' : '200px', // Ensure minimum width
                color: 'white', 
                cursor: 'pointer', 
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)', // Slightly softer shadow
                backgroundColor: colors?.main, 
                '&:hover': { backgroundColor: colors?.mainHover }
            }}
        >

            <AddIcon sx={{ color: 'white', marginRight: 1 }} />

            <Box>{t('createConsultation')}</Box>

        </Box>

      </Box>

  );

};

export default Info;